import clsx from "clsx";
import { PaginationState } from "_metronic/helpers";
import { useLang } from "_metronic/i18n/Metronici18n";
import "./DataTablePagination.scss";

type DataTablePaginationProps = {
  pagination: PaginationState;
  isLoading: boolean;
  onPageChange: (pageNumber: number | null) => void;
};

type PaginationItem =
  | {
      type: "page";
      pageNumber: number;
    }
  | {
      type: "ellipsis";
      key: string;
    };

const getFallbackTotalPages = (pagination: PaginationState) => {
  const pageNumbers =
    pagination.links
      ?.map((link) => link.pageNumber)
      .filter(
        (pageNumber): pageNumber is number =>
          typeof pageNumber === "number" && pageNumber > 0
      ) || [];

  if (!pageNumbers.length) {
    return pagination.pageNumber || 1;
  }

  return Math.max(...pageNumbers);
};

const buildPaginationItems = (
  currentPage: number,
  totalPages: number
): PaginationItem[] => {
  if (totalPages <= 8) {
    return Array.from({ length: totalPages }, (_, index) => ({
      type: "page" as const,
      pageNumber: index + 1,
    }));
  }

  const pages = new Set<number>([
    1,
    totalPages,
    currentPage - 2,
    currentPage - 1,
    currentPage,
    currentPage + 1,
    currentPage + 2,
  ]);

  if (currentPage <= 4) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
    pages.add(5);
  }

  if (currentPage >= totalPages - 3) {
    pages.add(totalPages - 1);
    pages.add(totalPages - 2);
    pages.add(totalPages - 3);
    pages.add(totalPages - 4);
  }

  const sortedPages = Array.from(pages)
    .filter((pageNumber) => pageNumber >= 1 && pageNumber <= totalPages)
    .sort((left, right) => left - right);

  return sortedPages.reduce<PaginationItem[]>((items, pageNumber, index) => {
    const previousPage = sortedPages[index - 1];

    if (previousPage && pageNumber - previousPage > 1) {
      items.push({
        type: "ellipsis",
        key: `ellipsis-${previousPage}-${pageNumber}`,
      });
    }

    items.push({
      type: "page",
      pageNumber,
    });

    return items;
  }, []);
};

const DataTablePagination = ({
  pagination,
  isLoading,
  onPageChange,
}: DataTablePaginationProps) => {
  const lang = useLang();
  const isEnglish = lang === "en";
  const totalPages = Math.max(
    1,
    pagination.lastPage || getFallbackTotalPages(pagination)
  );
  const currentPage = Math.min(
    Math.max(1, pagination.pageNumber || 1),
    totalPages
  );

  if (totalPages <= 1) {
    return null;
  }

  const pageItems = buildPaginationItems(currentPage, totalPages);
  const numberFormatter = new Intl.NumberFormat(isEnglish ? "en-US" : "tr-TR");
  const summaryLabel = isEnglish
    ? `Page ${numberFormatter.format(currentPage)} of ${numberFormatter.format(
        totalPages
      )}`
    : `Sayfa ${numberFormatter.format(currentPage)} / ${numberFormatter.format(
        totalPages
      )}`;
  const recordCountLabel =
    typeof pagination.total === "number"
      ? `${numberFormatter.format(pagination.total)} ${
          isEnglish ? "records" : "kayıt"
        }`
      : null;

  return (
    <div className="row data-table-pagination-row">
      <div className="col-sm-12 col-md-5 d-flex align-items-center justify-content-center justify-content-md-start">
        <div className="data-table-pagination__summary">
          <span className="data-table-pagination__summary-title">
            {summaryLabel}
          </span>
          {recordCountLabel && (
            <span className="data-table-pagination__summary-subtitle">
              {recordCountLabel}
            </span>
          )}
        </div>
      </div>

      <div className="col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end">
        <nav
          className="data-table-pagination"
          aria-label={isEnglish ? "Pagination" : "Sayfalama"}
        >
          <ul className="data-table-pagination__list">
            <li className="data-table-pagination__item">
              <button
                type="button"
                className="data-table-pagination__button data-table-pagination__button--nav"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={isLoading || currentPage === 1}
                aria-label={
                  isEnglish ? "Go to previous page" : "Önceki sayfaya git"
                }
              >
                <i
                  className="bi bi-chevron-left data-table-pagination__icon"
                  aria-hidden="true"
                />
                <span className="d-none d-sm-inline">
                  {isEnglish ? "Previous" : "Geri"}
                </span>
              </button>
            </li>

            {pageItems.map((item) => {
              if (item.type === "ellipsis") {
                return (
                  <li
                    key={item.key}
                    className="data-table-pagination__item data-table-pagination__item--ellipsis"
                    aria-hidden="true"
                  >
                    <span className="data-table-pagination__ellipsis">...</span>
                  </li>
                );
              }

              return (
                <li
                  key={item.pageNumber}
                  className={clsx(
                    "data-table-pagination__item data-table-pagination__item--page",
                    {
                      "data-table-pagination__item--active":
                        item.pageNumber === currentPage,
                    }
                  )}
                >
                  <button
                    type="button"
                    className="data-table-pagination__button"
                    onClick={() => onPageChange(item.pageNumber)}
                    disabled={isLoading}
                    aria-current={
                      item.pageNumber === currentPage ? "page" : undefined
                    }
                    aria-label={
                      isEnglish
                        ? `Go to page ${item.pageNumber}`
                        : `${item.pageNumber}. sayfaya git`
                    }
                  >
                    {item.pageNumber}
                  </button>
                </li>
              );
            })}

            <li className="data-table-pagination__item">
              <button
                type="button"
                className="data-table-pagination__button data-table-pagination__button--nav"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={isLoading || currentPage === totalPages}
                aria-label={
                  isEnglish ? "Go to next page" : "Sonraki sayfaya git"
                }
              >
                <span className="d-none d-sm-inline">
                  {isEnglish ? "Next" : "İleri"}
                </span>
                <i
                  className="bi bi-chevron-right data-table-pagination__icon"
                  aria-hidden="true"
                />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export { DataTablePagination };
